/**
 * API client with security measures.
 *
 * Serialization boundary (PO-2.1):
 * - Request bodies go out as snake_case (camelToSnake applied before JSON.stringify).
 * - Response bodies come back as camelCase (snakeToCamel applied to parsed JSON).
 * - FormData bodies and non-JSON requests pass through untouched.
 * - Backend wire format stays snake_case; frontend types stay camelCase.
 */
import { secureFetch } from './security';
import { camelToSnake, snakeToCamel } from './caseTransform';
import type { DayBucketsRequest } from './searchAvailability';
import type { components } from '../types/generated/api';
import type {
  ApiResponse,
  BookingStatus,
  FeedNoteKind,
  RecommendationListResponse,
  RecommendationRequest,
  SchoolCalendarEventListResponse,
  VacationCoverageSearchResponse,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await secureFetch(url, options);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const converted = snakeToCamel<{ message?: string; errors?: Record<string, string[]> }>(
          errorData,
        );
        return {
          data: null as unknown as T,
          success: false,
          message: converted.message || `Request failed with status ${response.status}`,
          errors: converted.errors,
          status: response.status,
        };
      }

      const raw = await response.json();
      const data = snakeToCamel<T>(raw);
      return { data, success: true };
    } catch (error) {
      return {
        data: null as unknown as T,
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  private jsonBody(body: unknown): string | undefined {
    if (body === undefined || body === null) return undefined;
    return JSON.stringify(camelToSnake(body));
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: this.jsonBody(body),
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: this.jsonBody(body),
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: this.jsonBody(body),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient(API_BASE);

// Typed API endpoints
export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  register: (data: unknown) =>
    api.post('/auth/register', data),
  verifyEmail: (code: string) =>
    api.post('/auth/verify-email', { code }),
  resendVerification: () =>
    api.post('/auth/resend-verification'),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),
  logout: () =>
    api.post('/auth/logout'),
};

export interface NannySearchRequest {
  languages?: string[];
  availabilityStatuses?: string[];
  availability?: DayBucketsRequest[];
  workTypes?: string[];
  zipCode?: string;
  englishRequired?: boolean;
  backgroundCheckCleared?: boolean;
  hourlyRateMin?: number;
  hourlyRateMax?: number;
  minYearsExperience?: number;
  page?: number;
  pageSize?: number;
}

export interface NannySearchResult {
  userId: string;
  displayName: string;
  headline: string | null;
  birthCountryIso: string | null;
  borough: string | null;
  hourlyRateMin: number | null;
  hourlyRateMax: number | null;
  yearsExperience: number | null;
  avgRating: number | null;
  totalReviews: number;
  isAvailable: boolean;
  backgroundCheckCleared: boolean;
  similarityScore: number | null;
  languages: string[];
  cuisines: string[];
  avatarUrl: string | null;
  allergyCertificationStatus: string | null;
  openToSharing: boolean | null;
  shareSlotStatus: string | null;
  currentShareChildAgeMonths: number | null;
}

export interface NannySearchResponse {
  results: NannySearchResult[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const searchApi = {
  searchNannies: (body: NannySearchRequest) =>
    api.post<NannySearchResponse>('/search/nannies', body),
};

export interface ParentProfileUpdatePayload {
  zipCode?: string | null;
  borough?: string | null;
  neighborhoodId?: string | null;
  careType?: string | null;
  specialNeedsDescription?: string | null;
  isPublic?: boolean;
  bio?: string | null;
  primaryLanguage?: string | null;
}

export interface NannyProfilePreview {
  profileExists: boolean;
  borough: string | null;
  languages: string[];
  yearsExperience: number | null;
}

export const profileApi = {
  getMyNannyProfile: () =>
    api.get('/profiles/nanny/me'),
  getNannyProfile: (userId: string) =>
    api.get(`/profiles/nanny/${userId}`),
  getNannyProfilePreview: (userId: string) =>
    api.get<NannyProfilePreview>(`/profiles/nanny/${userId}/preview`),
  updateNannyProfile: (data: unknown) =>
    api.patch('/profiles/nanny', data),
  getMyParentProfile: () =>
    api.get('/profiles/parent/me'),
  getParentProfile: (userId: string) =>
    api.get(`/profiles/parent/${userId}`),
  updateParentProfile: (data: ParentProfileUpdatePayload) =>
    api.patch('/profiles/parent', data),
};

export interface UserSelfUpdatePayload {
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export const userApi = {
  updateMe: (data: UserSelfUpdatePayload) =>
    api.patch('/users/me', data),
};

export interface MessagingParticipant {
  userId: string;
  displayName: string;
  unreadCount: number;
  lastReadAt: string | null;
  isMuted: boolean;
}

export interface MessagingConversation {
  id: string;
  title: string | null;
  participants: MessagingParticipant[];
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  lastMessageSenderId: string | null;
  createdAt: string;
}

export interface MessagingMessage {
  id: string;
  conversationId: string;
  senderId: string;
  body: string;
  messageType: string;
  isDeleted: boolean;
  createdAt: string;
}

export interface ProfilePhotoResponse {
  photoId: string;
  variants: {
    large: string;
    medium: string;
    small: string;
    thumb: string;
  };
}

export const uploadApi = {
  uploadProfilePhoto: async (file: File): Promise<ApiResponse<ProfilePhotoResponse>> => {
    const form = new FormData();
    form.append('file', file);
    const url = `${API_BASE}/uploads/profile-photo`;
    try {
      const response = await secureFetch(url, { method: 'POST', body: form });
      const raw = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          data: null as unknown as ProfilePhotoResponse,
          success: false,
          message: raw.detail || raw.message || `Upload failed (${response.status})`,
          errors: undefined,
        };
      }
      return { data: snakeToCamel<ProfilePhotoResponse>(raw), success: true };
    } catch (error) {
      return {
        data: null as unknown as ProfilePhotoResponse,
        success: false,
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  },
  deleteProfilePhoto: () => api.delete('/uploads/profile-photo'),
  setEmojiAvatar: (emoji: string) =>
    api.post<{ emojiAvatar: string }>('/uploads/emoji-avatar', { emoji }),
};

export const messageApi = {
  getThreads: () =>
    api.get<{
      items: MessagingConversation[];
      total: number;
      limit: number;
      offset: number;
    }>('/messaging/conversations'),
  getMessages: (conversationId: string, before?: string, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before) params.set('before_id', before);
    return api.get<{ messages: MessagingMessage[]; total: number; hasMore: boolean }>(
      `/messaging/conversations/${conversationId}/messages?${params.toString()}`,
    );
  },
  sendMessage: (conversationId: string, body: string) =>
    api.post<MessagingMessage>(
      `/messaging/conversations/${conversationId}/messages`,
      { body, messageType: 'text' },
    ),
  createConversation: (recipientId: string, initialMessage: string) =>
    api.post<{ conversationId: string; messageId: string }>(
      '/messaging/conversations',
      { participantId: recipientId, initialMessage },
    ),
  markRead: (conversationId: string) =>
    api.post(`/messaging/conversations/${conversationId}/read`),
  getUnreadCount: () =>
    api.get<{ count: number }>('/messaging/unread-count'),
};

export const endorsementApi = {
  create: (nannyId: string, data: unknown) =>
    api.post(`/nannies/${nannyId}/endorsements`, data),
  getForNanny: (nannyId: string) =>
    api.get(`/nannies/${nannyId}/endorsements`),
};

export interface MarketplaceProviderSummary {
  providerId: string;
  providerName: string;
  providerRating: number | null;
  providerRatingCount: number | null;
  estimatedDurationMin: number | null;
  retailPrice: number | null;
  language: string | null;
  relationshipType: string;
  completionVerification: string;
  languagesSupported: string[];
  providerLogoUrl: string | null;
}

export interface MarketplaceCredential {
  id: string;
  credentialType: string;
  displayName: string;
  shortDescription: string | null;
  iconKey: string | null;
  category: string;
  requiresExpiry: boolean;
  expiryMonths: number | null;
  providersAvailable: number;
  languagesAvailable: string[];
  startingPrice: number | null;
  bestProviderForUser: MarketplaceProviderSummary | null;
  completionsTotal: number;
  completions30d: number;
}

export interface MarketplaceListResponse {
  credentials: MarketplaceCredential[];
  total: number;
}

export interface MarketplaceDetailResponse {
  credential: MarketplaceCredential;
  longDescription: string | null;
  curriculumSummary: string[];
  providers: MarketplaceProviderSummary[];
}

export type MyCredentialStatus = components['schemas']['CredentialExpiryStatus'];

export interface MyCredentialItem {
  id: string;
  catalogId: string | null;
  catalogName: string;
  issuedAt: string | null;
  expiresAt: string | null;
  status: MyCredentialStatus;
  daysUntilExpiry: number | null;
}

export interface CertificationPurchaseResult {
  request: { id: string; status: string };
  /** Provider training URL, returned once; admin-controlled, so validate before rendering. */
  accessLink?: string | null;
}

export const credentialApi = {
  getMyCredentials: () =>
    api.get<MyCredentialItem[]>('/credentials/mine'),
  getMarketplace: (params: {
    language?: string;
    search?: string;
    expiryRequired?: boolean;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.language) qs.set('language', params.language);
    if (params.search) qs.set('search', params.search);
    if (params.expiryRequired != null)
      qs.set('expiry_required', String(params.expiryRequired));
    const query = qs.toString();
    return api.get<MarketplaceListResponse>(
      `/credentials/marketplace${query ? `?${query}` : ''}`,
    );
  },
  getMarketplaceDetail: (catalogId: string, language?: string) => {
    const qs = language ? `?language=${language}` : '';
    return api.get<MarketplaceDetailResponse>(
      `/credentials/marketplace/${catalogId}${qs}`,
    );
  },
  getProviders: (catalogId: string) =>
    api.get(`/credentials/providers/${catalogId}`),
  purchase: (data: { nannyId?: string; catalogId: string; providerId: string }) =>
    api.post<CertificationPurchaseResult>('/credentials/purchase', data),
  selfReport: (data: {
    catalogId: string;
    documentUrl?: string;
    issuedDate?: string;
    expiryDate?: string;
    issuingBody?: string;
  }) => api.post('/credentials/self-report', data),
};

export const referralApi = {
  getInfo: () =>
    api.get('/referrals'),
  generateCode: () =>
    api.post('/referrals/generate'),
};

// Bookings
export interface Booking {
  id: string;
  parentId: string;
  /** Resolved on the list path only (#1178); null on create/update responses. */
  familyDisplayName?: string | null;
  nannyId: string;
  startTime: string;
  endTime: string;
  status: BookingStatus;
  hourlyRate: number;
  totalAmount: number | null;
  paymentStatus: string;
  parentNotes: string | null;
  nannyNotes: string | null;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BookingListResponse {
  bookings: Booking[];
  total: number;
}

export const bookingApi = {
  list: (params: { statusFilter?: BookingStatus; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.statusFilter) qs.set('status_filter', params.statusFilter);
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.offset != null) qs.set('offset', String(params.offset));
    const query = qs.toString();
    return api.get<BookingListResponse>(`/bookings/${query ? `?${query}` : ''}`);
  },
};

// Nanny dashboard data (S5, #1177)
export interface NannyDashboardStats {
  pendingRequests: number;
  pendingRequestsNewToday: number;
  activeBookings: number;
  activeBookingsFamily: string | null;
  earningsThisMonth: number;
  earningsThisYear: number;
}

export interface FeedNote {
  id: string;
  kind: FeedNoteKind;
  entity: string;
  occurredAt: string;
}

export interface NotificationFeed {
  notes: FeedNote[];
}

export const dashboardApi = {
  getStats: () =>
    api.get<NannyDashboardStats>('/dashboard/stats'),
};

export const notificationApi = {
  getFeed: (limit?: number) => {
    const qs = new URLSearchParams();
    if (limit != null) qs.set('limit', String(limit));
    const query = qs.toString();
    return api.get<NotificationFeed>(`/notifications/feed${query ? `?${query}` : ''}`);
  },
};

// Vacation Coverage & Availability
export const vacationCoverageApi = {
  updatePayPolicy: (policy: string) =>
    api.patch('/vacation-coverage/pay-policy', { policy }),
  getCalendarEvents: (params?: { eventType?: string; upcomingOnly?: boolean }) => {
    const query = new URLSearchParams();
    if (params?.eventType) query.set('event_type', params.eventType);
    if (params?.upcomingOnly) query.set('upcoming_only', 'true');
    return api.get<SchoolCalendarEventListResponse>(
      `/vacation-coverage/calendar-events?${query.toString()}`,
    );
  },
  getNextBreak: () =>
    api.get('/vacation-coverage/next-break'),
  declareAvailability: (data: { calendarEventId: string; coverageTypes: string[]; notes?: string }) =>
    api.post('/vacation-coverage/availability', data),
  getMyAvailabilities: () =>
    api.get('/vacation-coverage/availability'),
  removeAvailability: (id: string) =>
    api.delete(`/vacation-coverage/availability/${id}`),
  searchCoverage: (data: unknown) =>
    api.post<VacationCoverageSearchResponse>('/vacation-coverage/search', data),
};

export const campaignApi = {
  getActiveBanner: () =>
    api.get('/campaigns/active-banner'),
};

export const connectionApi = {
  getMyConnections: () =>
    api.get('/connections'),
  getConnection: (id: string) =>
    api.get(`/connections/${id}`),
  updateVisibility: (connectionId: string, visibilityPref: string) =>
    api.patch(`/connections/${connectionId}/visibility`, { visibilityPref }),
};

export const shareApi = {
  // Share profile
  upsertProfile: (data: unknown) =>
    api.put('/share/profile', data),
  getOwnProfile: () =>
    api.get('/share/profile'),
  getNannyShareProfile: (nannyId: string) =>
    api.get(`/share/profile/${nannyId}`),
  // Arrangements
  createArrangement: (data: { nannyUserId: string; neighborhood?: string | null }) =>
    api.post('/share/arrangements', data),
  joinArrangement: (id: string) =>
    api.patch(`/share/arrangements/${id}/join`),
  dissolveArrangement: (id: string) =>
    api.patch(`/share/arrangements/${id}/dissolve`),
  getArrangements: () =>
    api.get('/share/arrangements'),
  // Family connections
  requestConnection: (connectionId: string) =>
    api.post(`/share/connections/${connectionId}/request`),
  acceptConnection: (connectionId: string) =>
    api.patch(`/share/connections/${connectionId}/accept`),
  disconnectConnection: (connectionId: string) =>
    api.patch(`/share/connections/${connectionId}/disconnect`),
  getConnections: () =>
    api.get('/share/connections'),
};

export const recommendationApi = {
  createRequest: (data: { inviteeName: string; inviteeEmail: string }) =>
    api.post('/recommendations/requests', data),
  submitRecommendation: (token: string, data: unknown) =>
    api.post(`/recommendations/submit/${token}`, data),
  getNannyRecommendations: (nannyUserId: string) =>
    api.get<RecommendationListResponse>(`/recommendations/nanny/${nannyUserId}`),
  getMyRecommendationRequests: () =>
    api.get<RecommendationRequest[]>('/recommendations/requests'),
};

export interface ContactPayload {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  message: string;
}

// Public contact form (no auth)
export const contactApi = {
  send: (payload: ContactPayload) =>
    api.post<{ status: 'sent' }>('/contact', payload),
};

// Shared neighborhood catalog (typeahead)
export const neighborhoodApi = {
  search: (q: string, limit = 10) =>
    api.get(
      `/neighborhoods/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    ),
};

// Nanny Onboarding
export const onboardingApi = {
  getStatus: () =>
    api.get('/onboarding/status'),
  saveScreen: (screenNum: number, data: unknown) =>
    api.post(`/onboarding/screen/${screenNum}`, data),
  getNeighborhoods: (borough?: string) =>
    api.get(`/onboarding/neighborhoods${borough ? `?borough=${borough}` : ''}`),
  publish: () =>
    api.post('/onboarding/publish'),
  getNudges: () =>
    api.get('/onboarding/nudges'),
  getReview: () =>
    api.get('/onboarding/review'),
  saveDraft: (screenNum: number, data: unknown) =>
    api.post('/onboarding/draft', { screenNum, data }),
};

// Parent Onboarding (PO-2: shell + PATCH-on-Continue plumbing)
export const parentOnboardingApi = {
  getStatus: () =>
    api.get('/parent-onboarding/status'),
  saveStep: (stepNum: number, data: unknown) =>
    api.post(`/parent-onboarding/step/${stepNum}`, { data }),
  saveDraft: (stepNum: number, data: unknown) =>
    api.post('/parent-onboarding/draft', { stepNum, data }),
  nearestZip: (lat: number, lng: number) =>
    api.get<{ zipCode: string; distanceMiles: number }>(
      `/parent-onboarding/nearest-zip?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`,
    ),
};
